import * as v2 from '@/core/services/api/v2';

/**
 * O mercado de peixe do negócio: a venda do ESTOQUE DA EMPRESA na tela `/business`.
 *
 * No V2 não existe mais a rota `fish-market/sell-all-fish` que aceitava um
 * `totalPrice` dito pelo cliente — vender é sempre `POST /sell` com **ids**, e
 * o preço é decidido no servidor. A prévia (`calculateFishValue`) usa a mesma
 * fórmula da venda, então o número mostrado é o número pago.
 */

const state = {
  marketPrices: null,
  fishValue: null,
  saleResults: null,
  isLoading: false
};

const mutations = {
  SET_MARKET_PRICES(state, prices) {
    state.marketPrices = prices;
  },
  SET_FISH_VALUE(state, value) {
    state.fishValue = value;
  },
  SET_SALE_RESULTS(state, results) {
    state.saleResults = results;
  },
  SET_LOADING(state, status) {
    state.isLoading = status;
  }
};

const actions = {
  /**
   * Sem chamador hoje — quem exibe o mercado é `market/updateMarketPrices`.
   * Fica como espelho fino de `v2.mercado()` para quem precisar do dado cru
   * sem passar pela store `market`.
   */
  async getMarketPrices({ commit }) {
    commit('SET_LOADING', true);
    try {
      const mercado = await v2.mercado();
      commit('SET_MARKET_PRICES', mercado);
      return mercado;
    } catch (error) {
      console.error(
        'Falha ao buscar o mercado:',
        error.response?.data?.message || error.response?.data?.error?.message || error.message,
      );
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Quanto vale o estoque da EMPRESA — a prévia honesta da venda.
   *
   * Desde 25/09/2026 o peixe da tripulação mora num armazém próprio
   * (`business_fish`), e não no inventário do jogador; este mercado do galpão
   * vende só ele. O peixe que o jogador pesca à mão continua sendo vendido pelo
   * inventário. A forma devolvida é a que `FishMarketModal` sempre leu.
   */
  async calculateFishValue({ commit }, playerId) {
    commit('SET_LOADING', true);
    try {
      const valor = await v2.estoqueDaEmpresa(playerId);
      commit('SET_FISH_VALUE', valor);
      return valor;
    } catch (error) {
      console.error(
        'Falha ao calcular o valor do estoque:',
        error.response?.data?.message || error.response?.data?.error?.message || error.message,
      );
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Vende o estoque inteiro da empresa. O servidor escolhe os peixes e o preço;
   * o envelope devolvido reproduz o que o modal sempre leu.
   */
  async sellAllFish({ commit, dispatch }, playerId) {
    commit('SET_LOADING', true);
    try {
      const resultado = await v2.venderEstoqueDaEmpresa(playerId);

      const venda = {
        totalGoldEarned: resultado.goldGained,
        newGoldBalance: resultado.gold,
        totalFishSold: resultado.sold,
        favoriteMap: null,
        charismaBonus: 1
      };
      commit('SET_SALE_RESULTS', venda);

      // O ouro mudou: o saldo que vale é o do servidor.
      await dispatch('player/loadPlayerState', playerId, { root: true });

      return venda;
    } catch (error) {
      console.error(
        'Falha ao vender o estoque:',
        error.response?.data?.message || error.response?.data?.error?.message || error.message,
      );
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  }
};

const getters = {
  getMarketPrices: state => state.marketPrices,
  getFishValue: state => state.fishValue,
  getSaleResults: state => state.saleResults,
  isLoading: state => state.isLoading
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
